import accepts from 'attr-accept';
export var ErrorCode;
(function (ErrorCode) {
    ErrorCode["FileInvlaidType"] = "file-invalid-type";
    ErrorCode["FileTooLarge"] = "file-too-large";
    ErrorCode["FileBundleTooLarge"] = "file-bundle-too-large";
    ErrorCode["FilenameIllegalCharacter"] = "filename-illegal-character";
    ErrorCode["FilenameMximumCharacter"] = "filename-maximum-character";
    ErrorCode["FileCountTooLarge"] = "file-count-too-large";
})(ErrorCode || (ErrorCode = {}));
const byteMultiplier = 1024;
const mapObj = {
    'application/msword': 'doc',
    'application/vnd.openxmlformats-officedocument.wordprocessingml.document': 'docx',
};
export const getInvalidTypeRejectionError = (accept, t) => {
    const regex = new RegExp(`\\b(?:${Object.keys(mapObj).join('|')})\\b`, 'gi');
    const correctedAcceptString = accept.replace(regex, (matched) => mapObj[matched]);
    return {
        code: ErrorCode.FileInvlaidType,
        message: t('common:fileUpload.errors.invalid', {
            ext: correctedAcceptString
                .split(',')
                .map((str) => str.substring(str.lastIndexOf('/') + 1, str.length).toLowerCase())
                .join(', '),
        }),
    };
};
export const getTooLargeRejectionError = (maxSize, t) => ({
    code: ErrorCode.FileTooLarge,
    message: t('common:fileUpload.errors.maxSize', {
        size: maxSize,
    }),
});
export const getFilenameIllegalCharacterError = (t) => ({
    code: ErrorCode.FilenameIllegalCharacter,
    message: t('common:fileUpload.errors.illegalCharacter'),
});
export const getFilenameMaximumCharacterError = (maxCharacter, t) => ({
    code: ErrorCode.FilenameMximumCharacter,
    message: t('common:fileUpload.errors.maxCharacter', {
        char: maxCharacter,
    }),
});
export function fileAccepted(file, accept, t) {
    const accepted = accepts(file, accept);
    return {
        accepted,
        acceptError: accepted ? null : getInvalidTypeRejectionError(accept, t),
    };
}
export function fileMatchSize(file, maxSize, t) {
    // Windows calculates bytes using 1024, while
    // Linux systems use 1000. As Zurich employees
    // use Windows, 1024 will be used for these calculations.
    const size = maxSize * byteMultiplier * byteMultiplier; // to bytes
    if (file.size && size) {
        if (file.size > size) {
            return {
                sizeMatch: false,
                sizeError: getTooLargeRejectionError(maxSize, t),
            };
        }
    }
    return {
        sizeMatch: true,
        sizeError: null,
    };
}
export const bytesTo = (unit, bytes) => {
    const units = ['Bytes', 'KB', 'MB', 'GB', 'TB'];
    if (bytes === 0) {
        return 0;
    }
    const index = units.findIndex((u) => u === unit);
    if (index === 0) {
        return bytes;
    }
    return Number.parseFloat((bytes / byteMultiplier ** index).toFixed(1));
};
export const calcBundleSize = (files) => files.reduce((accFileSize, file) => {
    if ('size' in file && typeof file.size === 'number') {
        return accFileSize + file.size;
    }
    return accFileSize;
}, 0);
export const calcBundleSizeInMB = (files) => {
    const bundleSize = calcBundleSize(files);
    if (bundleSize > 0) {
        const mb = bytesTo('MB', bundleSize);
        return mb.toFixed(mb > 1 ? 0 : 1);
    }
    return 0;
};
export function fileBundleMatchSize(files, maxBundleSize, t) {
    const maxSize = maxBundleSize * byteMultiplier * byteMultiplier; // to bytes
    const bundleSize = calcBundleSize(files);
    if (bundleSize > maxSize) {
        return {
            bundleSizeError: {
                message: t('common:fileUpload.errors.maxBundleSize', {
                    size: maxBundleSize,
                }),
                code: ErrorCode.FileBundleTooLarge,
            },
        };
    }
    return {
        bundleSizeError: null,
    };
}
export function fileBundleMaxFileCount(files, maxFileCount, t) {
    if (maxFileCount !== undefined && files.length > maxFileCount) {
        return {
            fileCountError: {
                message: t('common:fileUpload.errors.maxFileCount', {
                    maxFileCount,
                }),
                code: ErrorCode.FileCountTooLarge,
            },
        };
    }
    return {
        fileCountError: null,
    };
}
export function filenameMaximumSize(file, fileCharacterSize, t) {
    const matcher = new RegExp(`^(.{1,${fileCharacterSize}})\\.(\\w+)`);
    const filenameSizeMatch = matcher.test(file.name);
    return {
        filenameSizeMatch,
        filenameSizeError: filenameSizeMatch
            ? null
            : getFilenameMaximumCharacterError(fileCharacterSize, t),
    };
}
export const containsIllegalCharacters = (fileName, acceptedFileNameRegex) => /[\\/:*?"<>|]+/.test(fileName) || acceptedFileNameRegex
    ? !acceptedFileNameRegex?.test(fileName)
    : false;
export function filenameIllegalCharacter(file, acceptedFileNameRegex, t) {
    const filenameMatch = !containsIllegalCharacters(file.name, acceptedFileNameRegex);
    return {
        filenameMatch,
        filenameError: filenameMatch ? null : getFilenameIllegalCharacterError(t),
    };
}
